import React from 'react';
import {
  CalendarBlank, ChartLineUp, CheckCircle, Lightbulb, ListBullets,
  ListChecks, PaperPlaneTilt, Sparkle, Target,
} from '@phosphor-icons/react';

const COLOR_CLASS = {
  blue: 'athena-response-color-blue',
  lightBlue: 'athena-response-color-light-blue',
  yellow: 'athena-response-color-yellow',
  rose: 'athena-response-color-rose',
  green: 'athena-response-color-green',
};

const EDITORIAL_SECTION_VISUALS = [
  { key: 'strengths', test: /pontos? fortes?|for[cç]a|potencial|vantagem/i, tone: 'green', Icon: CheckCircle },
  { key: 'risks', test: /pontos? fracos?|risco|aten[cç][aã]o|gargalo|limita[cç][aã]o/i, tone: 'rose', Icon: ChartLineUp },
  { key: 'objective', test: /objetiv|meta|foco|prioridad|dire[cç][aã]o/i, tone: 'yellow', Icon: Target },
  { key: 'ideas', test: /ideia|insight|conte[uú]do|estrat[eé]g|sugest|oportunidade/i, tone: 'blue', Icon: Lightbulb },
  { key: 'schedule', test: /plano|agenda|semana|cronograma|etapa|roteiro|organiza/i, tone: 'violet', Icon: CalendarBlank },
  { key: 'example', test: /exemplo|modelo|post|rascunho|mensagem|aplica[cç][aã]o/i, tone: 'rose', Icon: PaperPlaneTilt },
  { key: 'next', test: /pr[oó]ximo|a[cç][aã]o|passo|conclus[aã]o|recomenda[cç][aã]o|come[cç]ar/i, tone: 'green', Icon: CheckCircle },
  { key: 'analysis', test: /an[aá]lis|diagn[oó]stico|ponto|leitura|cen[aá]rio/i, tone: 'blue', Icon: ChartLineUp },
];

function sectionVisual(title, index) {
  const found = EDITORIAL_SECTION_VISUALS.find(item => item.test.test(title || ''));
  if (found) return found;
  const defaults = [
    { key: 'spark', tone: 'blue', Icon: Sparkle },
    { key: 'list', tone: 'violet', Icon: ListChecks },
    { key: 'next', tone: 'green', Icon: CheckCircle },
  ];
  return defaults[index % defaults.length];
}

function InlineSegment({ segment }) {
  if (!segment?.text) return null;
  if (segment.type === 'bold') return <strong>{segment.text}</strong>;
  if (segment.type === 'highlight') {
    return <mark className={`athena-response-highlight ${COLOR_CLASS[segment.color] || ''}`}>{segment.text}</mark>;
  }
  if (segment.type === 'colored') {
    return <span className={COLOR_CLASS[segment.color] || 'athena-response-color-blue'}>{segment.text}</span>;
  }
  if (segment.type === 'link') {
    return <a href={segment.href} target="_blank" rel="noopener noreferrer">{segment.text}</a>;
  }
  return <>{segment.text}</>;
}

export function InlineContent({ content = [] }) {
  return <>{content.map((segment, index) => <InlineSegment key={`${segment.type}-${index}-${segment.text}`} segment={segment} />)}</>;
}

export function ResponseBlocks({ blocks = [], compact = false, editorial = false }) {
  return (
    <div className={`athena-response-blocks ${compact ? 'is-compact' : ''} ${editorial ? 'is-editorial' : ''}`}>
      {blocks.map((block, index) => {
        const key = `${block.type}-${index}`;
        if (block.type === 'divider') return <hr key={key} className="athena-response-divider" />;
        if (block.type === 'heading') {
          const Tag = `h${block.level || 2}`;
          return <Tag key={key} className={`athena-response-heading h${block.level || 2}`}><InlineContent content={block.content} /></Tag>;
        }
        if (block.type === 'quote') return <blockquote key={key}><InlineContent content={block.content} /></blockquote>;
        if (block.type === 'meta') return (
          <div key={key} className="athena-response-meta">
            <span>{block.label}</span><p><InlineContent content={block.content} /></p>
          </div>
        );
        if (block.type === 'list') {
          const Ordered = block.style === 'ordered' ? 'ol' : 'ul';
          return (
            <Ordered key={key} className={`athena-response-list ${block.style === 'check' ? 'is-checklist' : ''} ${editorial ? 'is-editorial' : ''}`}>
              {block.items.map((item, itemIndex) => {
                return (
                <li key={`${key}-${itemIndex}`}>
                  {block.style === 'check' && <CheckCircle size={16} weight={item.checked ? 'fill' : 'regular'} aria-hidden="true" />}
                  {block.style === 'ordered'
                    ? <span className="athena-response-list-index" aria-hidden="true">{itemIndex + 1}</span>
                    : block.style !== 'check' && (editorial
                      ? <ListBullets size={14} aria-hidden="true" className={`athena-response-list-icon tone-index-${itemIndex % 5}`} />
                      : <ListBullets size={14} aria-hidden="true" className="athena-response-list-icon" />)}
                  <span><InlineContent content={item.content} /></span>
                </li>
                );
              })}
            </Ordered>
          );
        }
        return <p key={key}><InlineContent content={block.content} /></p>;
      })}
    </div>
  );
}

function EditorialSectionHeader({ section, index }) {
  const visual = sectionVisual(section.title, index);
  const Icon = visual.Icon;
  return (
    <header className="athena-editorial-section-header" data-tone={visual.tone}>
      <span className="athena-editorial-section-icon"><Icon size={23} weight="regular" aria-hidden="true" /></span>
      <span className="athena-editorial-section-number" aria-hidden="true">{index + 1}.</span>
      <h2>{section.title}</h2>
    </header>
  );
}

export function ResponseSections({ sections = [], compact = false, variant = 'default' }) {
  const editorial = variant === 'editorial';
  return (
    <div className={`athena-response-sections ${editorial ? 'is-editorial' : ''} ${compact ? 'is-compact' : ''}`}>
      {sections.map((section, index) => (
        <section key={section.id || index} className="athena-response-section">
          {editorial && section.title
            ? <EditorialSectionHeader section={section} index={index} />
            : section.title && <h2>{section.title}</h2>}
          <ResponseBlocks blocks={section.blocks} compact={compact} editorial={editorial} />
        </section>
      ))}
    </div>
  );
}
